import { UseFormReturn } from 'react-hook-form'
import { tz, TZDate } from '@date-fns/tz'
import {
  format,
  getDate,
  getHours,
  getMilliseconds,
  getMinutes,
  getMonth,
  getSeconds,
  getYear,
  isDate,
  isSameMonth,
  isSameDay,
  isThisMonth,
  set,
} from 'date-fns'
import { formatDate } from '@unimpaired/utils'
import * as chrono from 'chrono-node'

export type BaseProps = {
  name?: string
  className?: string
  timezone?: string
}

export type UseFormProps<T extends BaseProps> = T & {
  form: UseFormReturn
  defaultValue?: Date
}

export type UseStateProps<T extends BaseProps> = T & {
  date: Date
  setDate: (date: Date | undefined) => void
}

export type Props<T extends BaseProps = BaseProps> =
  | UseStateProps<T>
  | UseFormProps<T>

export const formExists = <T extends BaseProps>(
  props: Partial<Props>,
): props is UseFormProps<T> => 'form' in props

export const stateExists = <T extends BaseProps>(
  props: Partial<Props>,
): props is UseStateProps<T> => 'date' in props

export const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
export const allTimezones = Intl.supportedValuesOf('timeZone')

// `as const` keeps the tuple literal so DatePart derives from it (no cast).
const datePartNames = [
  'year',
  'month',
  'date',
  'hours',
  'minutes',
  'seconds',
  'milliseconds',
] as const
type DatePart = (typeof datePartNames)[number]

const datePartGetters: Record<DatePart, (date: Date) => number> = {
  year: getYear,
  month: getMonth,
  date: getDate,
  hours: getHours,
  minutes: getMinutes,
  seconds: getSeconds,
  milliseconds: getMilliseconds,
}

export const datePartsEqual = (
  date1: Date,
  date2: Date,
  parts: Partial<Record<DatePart, boolean>>,
) =>
  // Every part flagged `true` must read equal on both dates.
  datePartNames
    .filter((part) => parts[part] === true)
    .every((part) => {
      const get = datePartGetters[part]
      return get(date1) === get(date2)
    })

export const removeSecondsMilliseconds = (date: Date) =>
  set(date, {
    seconds: 0,
    milliseconds: 0,
  })

// Pure helpers extracted from the date pickers; behavior-preserving.

export type Venue = { timezone: string }

/** `{ in: tz(...) }` anchors day-rounding helpers to the venue TZ; undefined
 *  means "use the runtime TZ". */
export const buildTzOptions = (venue?: Venue) =>
  venue ? { in: tz(venue.timezone) } : undefined

/** Is `date` one of the selected `dates`, compared at venue-day granularity. */
export const isSelectedInDates = (date: Date, dates: Date[], venue?: Venue) => {
  const tzOptions = buildTzOptions(venue)
  return dates.some((d) => isSameDay(date, d, tzOptions))
}

/** Same calendar month as `month`, anchored to the venue TZ. */
export const isSameVenueMonth = (date: Date, month: Date, venue?: Venue) =>
  isSameMonth(date, month, buildTzOptions(venue))

/** Sort by numeric instant — a comparator-less Date.sort() is lexicographic. */
const earliestDate = (dates: Date[]) =>
  [...dates].sort((a, b) => a.getTime() - b.getTime())[0]

/** If any selected date is in the current venue-month, default to today;
 *  otherwise jump to the earliest selected date (so the grid shows something). */
export const pickDefaultMonth = (dates: Date[], venue?: Venue) => {
  const tzOptions = buildTzOptions(venue)
  return dates.find((d) => isThisMonth(d, tzOptions))
    ? new Date()
    : earliestDate(dates) ?? new Date()
}

/** Parse `text` on the venue clock. An abbreviation ("PDT") is DST-blind and
 *  unknown to chrono for most zones, so it gets the numeric offset (minutes
 *  EAST of UTC; TZDate reports WEST) at `instant` (a NATIVE Date — a TZDate
 *  breaks its parser), and TZDate resolves the parsed wall clock's DST. */
export const parseDateInTimezone = (
  parser: chrono.Chrono,
  text: string,
  instant: Date,
  timezone: string,
  options?: chrono.ParsingOption,
) => {
  const offset = -new TZDate(instant, timezone).getTimezoneOffset()
  const reference = { instant: new Date(instant.getTime()), timezone: offset }
  const [result] = parser.parse(text, reference, options)
  if (!result) return null
  const part = (component: chrono.Component) => result.start.get(component) ?? 0
  // an explicit zone in the text ("7pm EST") wins
  const parsed = result.start.isCertain('timezoneOffset')
    ? result.start.date()
    : new TZDate(
        part('year'),
        part('month') - 1,
        part('day'),
        part('hour'),
        part('minute'),
        part('second'),
        timezone,
      )
  return new Date(parsed.getTime())
}

/** Returns a TZDate anchored to `timezone` so day math stays venue-correct. */
export const parseNaturalLanguageDate = (
  value: Date | string,
  timezone: string = userTimezone,
) => {
  const instant = new TZDate(new Date(), timezone)
  return new TZDate(
    isDate(value)
      ? value
      : parseDateInTimezone(chrono.casual, value, instant, timezone) ?? instant,
    timezone,
  )
}

/** Copy year/month/day from `source` onto `target`, keeping target's time. */
export const spliceDayKeepTime = (
  target: Date | string | number,
  source: Date,
) =>
  set(target, {
    date: source.getDate(),
    month: source.getMonth(),
    year: source.getFullYear(),
  })

/** Copy time-of-day from `source` onto `date`. */
export const applyTimeOfDay = (date: Date, source: Date) =>
  set(date, {
    hours: source.getHours(),
    minutes: source.getMinutes(),
    seconds: source.getSeconds(),
    milliseconds: source.getMilliseconds(),
  })

/** DateSelect input format: passthrough non-dates, customFormat if given, else
 *  readable on desktop / ISO day on mobile. */
export const formatDateValue = (
  value: unknown,
  options: {
    isMobile: boolean
    customFormat?: (date: Date) => unknown
    timezone?: string
  },
): unknown => {
  if (!isDate(value)) return value
  if (options.customFormat) return options.customFormat(value)
  const timezone = options.timezone ?? userTimezone
  return options.isMobile
    ? format(value, 'yyyy-MM-dd', { in: tz(timezone) })
    : formatDate('readable', value, timezone)
}
